import {
    Avatar,
    Box,
    Grid,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import data from '../../../data/data.json';
import './stats.css';
import { Link } from 'react-router-dom';

const StatsView = () => {
    const theme = useTheme();
    const {t} = useTranslation();

    const clickDownload = useCallback(() => {
        window.open(data.store.play_store, "_blank");
    }, []);

    return (
        <>
            <Stack
                position='relative'
                direction='column'
                justifyContent='center'
                alignItems='center'
                sx={{
                    backgroundColor: 'white'
                }}
            >
                {/* Upper cloud */}
                <Box
                    sx={{
                        width: '100%',
                        zIndex: 1,
                        backgroundColor: theme.palette.primary.main,
                        transform: 'scaleY(-1) translateY(2px)',
                    }}
                >
                    <img 
                        src='/images/vectors/wave bg.png' 
                        alt='cloud' 
                        width='100%' 
                        data-aos="fade-down"
                        data-aos-duration="500"
                        data-aos-once="true"
                    />
                </Box>

                {/* Body */}
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    px={{xs: 3, md: 15, lg: 25}}
                    sx={{
                        zIndex: 2
                    }}
                    spacing={2}
                >
                    {/* About */}
                    <Grid
                        direction='row'
                        justifyContent='start'
                        alignItems='start'
                        container
                    >
                        {/* Text */}
                        <Grid size={{xs: 12, md: 7}}>
                            <Stack
                                direction='column'
                                justifyContent='center'
                                alignItems='start'
                            >
                                {/* About */}
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: {xs: 45, md: 60, lg: 75},
                                        color: theme.palette.primary.main,
                                    }}
                                    data-aos="fade-up"
                                    data-aos-duration="1000"
                                >
                                    {t('stats.about')}
                                </Typography>
                                
                                {/* Title */}
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: {xs: 30, md: 45, lg: 60},
                                        color: theme.palette.secondary.main,
                                        lineHeight: 1,
                                        mt: {xs: -1, md: -2}
                                    }}
                                    data-aos="fade-up"
                                    data-aos-delay="100"
                                    data-aos-duration="1000"
                                >
                                    {t('game')}
                                </Typography>

                                {/* Description */}
                                <Typography
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: {xs: 12, md: 14, lg: 16},
                                        pt: {xs: 1.5, md: 3},
                                        textAlign: {xs: 'justify', md: 'left'}
                                    }}
                                    data-aos="fade-up"
                                    data-aos-delay="200"
                                    data-aos-duration="1000"
                                >
                                    {t('stats.description')}
                                </Typography>
                            </Stack>
                        </Grid>

                        {/* Image */}
                        <Grid 
                            size={{xs: 12, md: 5}}
                            display={{xs: 'none', md: 'flex'}}
                            direction='row'
                            justifyContent='center'
                            data-aos="flip-left"
                            data-aos-delay="400"
                            data-aos-duration="1000"
                        >
                            <img 
                                src='/images/game/Stats banner-1.png' 
                                alt='penguin' 
                                width='75%' 
                                style={{
                                    justifyContent: 'start',
                                    animation: 'statsBanner1 5s infinite linear'
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Stats */}
                    <Grid
                        direction='row'
                        justifyContent='start'
                        alignItems='start'
                        container
                        width='100%'
                    >
                        {/* Stats */}
                        <Grid 
                            size={{xs: 12, md: 7}}
                            container
                            spacing={{xs: 1, md: 3, lg: 5}}
                            direction='row'
                            justifyContent='center'
                        >
                            <Grid
                                direction='row'
                                justifyContent='start'
                                alignItems='end'
                                container
                                width='100%'
                            >
                                <Grid 
                                    size={6} 
                                    data-aos="fade-up"
                                    data-aos-delay="300"
                                    data-aos-duration="1000"
                                    display='flex'
                                    direction='row'
                                    justifyContent={{xs: 'center', md: 'start'}}
                                >
                                    <Stat 
                                        value={data.stats.stat1.value} 
                                        suffix={data.stats.stat1.suffix}
                                        description={t('stats.stat-1')}
                                        icon={data.stats.stat1.icon}
                                    />
                                </Grid>

                                <Grid 
                                    size={6} 
                                    data-aos="fade-up"
                                    data-aos-delay="400"
                                    data-aos-duration="1000"
                                    display='flex'
                                    direction='row'
                                    justifyContent={{xs: 'center', md: 'start'}}
                                >    
                                    <Stat 
                                        value={data.stats.stat2.value} 
                                        suffix={data.stats.stat2.suffix}
                                        description={t('stats.stat-2')}
                                        icon={data.stats.stat2.icon}
                                    />
                                </Grid>
                            </Grid>

                            <Grid
                                direction='row'
                                justifyContent='start'
                                alignItems='end'
                                container
                                width='100%'
                            >
                                <Grid 
                                    size={6} 
                                    data-aos="fade-up"
                                    data-aos-delay="500"
                                    data-aos-duration="1000"
                                    display='flex'
                                    direction='row'
                                    justifyContent={{xs: 'center', md: 'start'}}
                                >
                                    <Stat 
                                        value={data.stats.stat3.value} 
                                        suffix={data.stats.stat3.suffix}
                                        description={t('stats.stat-3')}
                                        icon={data.stats.stat3.icon}
                                    />
                                </Grid>

                                <Grid 
                                    size={6} 
                                    data-aos="fade-up"
                                    data-aos-delay="600"
                                    data-aos-duration="1000"
                                    display='flex'
                                    direction='row'
                                    justifyContent={{xs: 'center', md: 'start'}}
                                >
                                    <Stat 
                                        value={data.stats.stat4.value} 
                                        suffix={data.stats.stat4.suffix}
                                        description={t('stats.stat-4')}
                                        icon={data.stats.stat4.icon}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Image */}
                        <Grid 
                            size={{xs: 12, md: 5}}
                            display={{xs: 'none', md: 'flex'}}
                            direction='row'
                            justifyContent='center'
                            data-aos="flip-left"
                            data-aos-delay="500"
                            data-aos-duration="1000"
                        >
                            <img 
                                src='/images/game/Stats banner-2.png' 
                                alt='penguin' 
                                width='75%' 
                                style={{
                                    justifyContent: 'start',
                                    animation: 'statsBanner2 5s infinite linear'
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Only for mobile view */}
                    <Grid
                        direction='row'
                        justifyContent='center'
                        alignItems='center'
                        display={{xs: 'flex', md: 'none'}}
                        container
                        spacing={2}
                        pt={2}
                    >
                        <Grid 
                            size={6}
                            display='flex'
                            justifyContent='end'
                            data-aos="flip-left"
                            data-aos-delay="700"
                            data-aos-duration="1000"
                        >
                            <img 
                                src='/images/game/Stats banner-1.png' 
                                alt='penguin' 
                                width='75%' 
                                style={{
                                    animation: 'statsBanner1 5s infinite linear'
                                }}
                            />
                        </Grid>

                        <Grid 
                            size={6}
                            data-aos="flip-right"
                            data-aos-delay="700"
                            data-aos-duration="1000"
                        >
                            <img 
                                src='/images/game/Stats banner-2.png' 
                                alt='penguin' 
                                width='75%' 
                                style={{
                                    animation: 'statsBanner2 5s infinite linear'
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Download */}
                    <Link
                        to={data.store.play_store}
                        target="_blank"
                    >
                        <Box
                            sx={{
                                width: 200,
                                '&: hover': {
                                    transform: 'scale(0.9)',
                                    transform: {xs: 'translateY(20px)', md: 'translateY(50px)', lg: 'translateY(100px)'}
                                },
                                animation: 'downloadBtn 2s infinite 1s',
                                transform: {xs: 'translateY(20px)', md: 'translateY(50px)', lg: 'translateY(100px)'}
                            }}
                        >
                            <img 
                                src="/images/icons/Playstore icon.png" 
                                alt="Play store" 
                                width='100%'
                            />
                        </Box>
                    </Link>
                </Stack>

                {/* Lower cloud */}
                <Box
                    sx={{
                        width: '100%',
                        zIndex: 1,
                        backgroundColor: theme.palette.primary.main,
                        transform: 'translateY(2px)'
                    }}
                >
                    <img 
                        src='/images/vectors/wave bg.png' 
                        alt='cloud' 
                        width='100%' 
                        data-aos="fade-down"
                        data-aos-duration="500"
                        data-aos-once="true"
                    />
                </Box>
            </Stack>
        </>
    );
}

export default StatsView;

const Stat = ({value, suffix, description, icon}) => {
    const theme = useTheme();

    return (
        <>
            <Stack
                direction='column'
                justifyContent='start'
                alignItems={{xs: 'center', md: 'start'}}
            >
                {
                    !icon
                    ?
                    <Stack
                        direction='row'
                        justifyContent='start'
                        alignItems='start'
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: {xs: 45, md: 60, lg: 75},
                                color: theme.palette.primary.main
                            }}
                        >
                            {value}
                        </Typography>

                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: {xs: 45, md: 60, lg: 75},
                                color: theme.palette.secondary.main
                            }}
                        >
                            {suffix}
                        </Typography>
                    </Stack>
                    :
                    <Avatar
                        alt={description}
                        src={icon}
                        sx={{ width: 115, height: 115 }}
                    />
                }

                <Typography
                    sx={{
                        fontWeight: 400,
                        fontSize: {xs: 12, md: 14, lg: 16},
                    }}
                >
                    {description}
                </Typography>
            </Stack>
        </>
    );
}